// ExecuteDialog — simple confirmation dialog for running an import.
// Uses the standard OIDC session for authentication (no API key needed).
// Triggers a silent token renew before execution to ensure a fresh token
// for the loopback calls.

import { useMutation } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { useApi } from '@/lib/api/client'
import { importsApi } from '@/lib/api/imports'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/lib/hooks/use-toast'
import { AlertTriangle, Loader2, Play } from 'lucide-react'

// --- Props ---

interface ExecuteDialogProps {
  open: boolean
  importId: string
  onClose: () => void
  onComplete: () => void
}

// --- Component ---

export function ExecuteDialog({ open, importId, onClose, onComplete }: ExecuteDialogProps) {
  const api = useApi()
  const auth = useAuth()
  const importApiClient = importsApi(api)
  const { toast } = useToast()

  const executeMutation = useMutation({
    mutationFn: async () => {
      // Ensure fresh token before long-running operation.
      // The loopback client forwards this token for each individual API call.
      try {
        await auth.signinSilent()
      } catch {
        // signinSilent can fail if the IdP is unreachable or session expired.
        // Fall through — the existing token may still be valid enough.
      }
      return importApiClient.execute(importId)
    },
    onSuccess: () => {
      toast({ title: 'Import executed successfully' })
      onComplete()
    },
    onError: (err: Error) => {
      toast({
        title: 'Import execution failed',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  const isExecuting = executeMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isExecuting) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Execute Import</DialogTitle>
          <DialogDescription>
            This will create the objects defined in the import file using your permissions.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            Objects will be created with your current role and permissions.
            Any items you don't have permission to create will be skipped.
          </p>
        </div>

        {executeMutation.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{executeMutation.error.message}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isExecuting}>
            Cancel
          </Button>
          <Button onClick={() => executeMutation.mutate()} disabled={isExecuting}>
            {isExecuting ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Executing...</>
            ) : (
              <><Play className="mr-1.5 h-4 w-4" />Execute Import</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
